import { Controller } from "@hotwired/stimulus"

// TomSelect emits one change per item, including while it restores the
// server-rendered selection on connect, so company changes are coalesced
// before the folder frame is reloaded.
const COMPANY_CHANGE_DEBOUNCE_MS = 250

export default class extends Controller {
  static targets = ["frame"]
  static values = {
    url: String,
    companyId: { type: String, default: "" }
  }

  disconnect() {
    clearTimeout(this.companyChangeTimeout)
  }

  companyChanged(event) {
    // TomSelect's search box sits alongside the real select and emits its own
    // change events on blur.
    if (event.target.tagName !== 'SELECT') return

    const companyId = event.target.value || ''
    this.syncFolderSelect(companyId)

    clearTimeout(this.companyChangeTimeout)
    this.companyChangeTimeout = setTimeout(() => {
      this.reloadFolders(companyId)
    }, COMPANY_CHANGE_DEBOUNCE_MS)
  }

  // The previous company's folders stay in the form until the frame reloads.
  // Disable them immediately so submit cannot pair a new company with a stale folder.
  syncFolderSelect(companyId) {
    const select = this.folderSelect()
    if (!select) return

    if (companyId === this.companyIdValue) {
      select.disabled = false
      return
    }

    select.value = ""
    select.disabled = true
  }

  folderSelect() {
    if (!this.hasFrameTarget) return null

    return this.frameTarget.querySelector("#folder_id")
  }

  // Reloading discards the current folder, so skip it when the company ended up
  // unchanged (TomSelect replaying its initial item, or a reselection).
  reloadFolders(companyId) {
    if (!this.hasFrameTarget || !this.hasUrlValue) return
    if (companyId === (this.pendingCompanyId ?? this.companyIdValue)) return

    this.pendingCompanyId = companyId

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('company_id', companyId)
    this.frameTarget.src = url.toString()
  }

  // companyIdValue tracks what the frame is actually showing, so it is only
  // committed once the new options render. Turbo cancels a superseded request
  // without erroring, so the last navigation to finish is the current one.
  foldersLoaded() {
    if (this.pendingCompanyId != null) {
      this.companyIdValue = this.pendingCompanyId
      this.pendingCompanyId = null
    }
  }

  // Without this the tracked company would stay ahead of the rendered one and
  // reselecting it would be treated as a no-op.
  foldersFailed() {
    this.pendingCompanyId = null
  }
}
